
/**
 * CorrectiveAction Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class CorrectiveActionService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const pagination = response.pagination || response.meta || {};

            return {
                success: true,
                data: response.data,
                pendingCreateRows: response.pendingCreateRows || [],
                pagination,
                meta: pagination,
                uniqueValues: response.uniqueValues,
                message: response.message || 'Aksiyon listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch corrective-action list');
        }
    }

    async getStats() {
        try {
            const response = await withRetry(
                () => this.api.getStats(),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data || response,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch action stats');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch corrective-action');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    /**
     * @param {Object} data
     * @param {{ pageKey?: string }} [options] - Pass `pageKey: 'auditComplianceCapa'` for CAPA list context (permissions + pending rows)
     */
    async create(data, options = {}) {
        const payload = data;
        const query = options?.pageKey ? { pageKey: options.pageKey } : {};

        try {
            const response = await this.api.create(payload, query);

            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Aksiyon oluşturuldu',
                pendingChangeId: response.pendingChangeId,
                approvalStatus: response.approvalStatus,
                merged: response.merged === true,
                draftExecutionTicketId: response.draftExecutionTicketId,
                restarted: response.restarted === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create corrective-action');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @param {{ pageKey?: string }} [options]
     */
    async update(id, data, options = {}) {
        const payload = data;
        const query = options?.pageKey ? { pageKey: options.pageKey } : {};

        try {
            const response = await this.api.update(id, payload, query);

            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Aksiyon güncellendi',
                pendingChangeId: response.pendingChangeId,
                approvalStatus: response.approvalStatus,
                merged: response.merged === true,
                draftExecutionTicketId: response.draftExecutionTicketId,
                restarted: response.restarted === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update corrective-action');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @param {{ pageKey?: string }} [options]
     */
    async delete(id, options = {}) {
        const query = options?.pageKey ? { pageKey: options.pageKey } : {};

        try {
            const response = await this.api.delete(id, query);

            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Aksiyon silindi',
                pendingChangeId: response.pendingChangeId,
                approvalStatus: response.approvalStatus,
                merged: response.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete corrective-action');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @param {{ pageKey?: string }} [options]
     */
    async bulkDelete(ids, options = {}) {
        const query = options?.pageKey ? { pageKey: options.pageKey } : {};

        try {
            const response = await this.api.batchDelete(ids, query);
            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to perform bulk delete');
        }
    }

    /**
     * Custom operation: createVerify
     */
    async createVerify(id, data = {}) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/verify`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createVerify');
        }
    }

    async createBatchDelete(data = {}) {
        const query = data?.pageKey ? { pageKey: data.pageKey } : {};

        try {
            const response = await this.api.batchDelete(data.ids || [], query);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    async getExecutionStats() {
        try {
            const response = await withRetry(() => this.api.getExecutionStats(), { maxRetries: 2 });
            return { success: true, data: response.data ?? response };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch execution KPIs');
        }
    }

    async getExecutionTicket(id) {
        try {
            const response = await withRetry(() => this.api.getExecutionTicket(id), { maxRetries: 2 });
            return { success: true, data: response.data ?? response };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch execution ticket');
        }
    }

    async openExecutionTicket(id) {
        try {
            const response = await withRetry(() => this.api.openExecutionTicket(id), { maxRetries: 2 });
            return { success: true, data: response.data ?? response };
        } catch (error) {
            throw this._handleError(error, 'Failed to open execution ticket');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
